import type { DashboardMetricsParams } from "@/domain/dashboard";
import type { TemplateFilters } from "@/domain/template";

export const queryKeys = {
	admin: {
		all: () => ["admin"] as const,
		dashboard: () => ["admin", "dashboard"] as const,
		organizations: (params?: Record<string, unknown>) =>
			["admin", "organizations", params] as const,
		organizationDetail: (uuid: string) =>
			["admin", "organizations", "detail", uuid] as const,
		organizationMembers: (uuid: string) =>
			["admin", "organizations", "members", uuid] as const,
		organizationInvitations: (uuid: string) =>
			["admin", "organizations", "invitations", uuid] as const,
		users: (params?: Record<string, unknown>) =>
			["admin", "users", params] as const,
		userDetail: (uuid: string) => ["admin", "users", "detail", uuid] as const,
		userMemberships: (uuid: string) =>
			["admin", "users", "memberships", uuid] as const,
	},
	investments: {
		all: (orgId: string) => ["investments", orgId] as const,
		list: (
			orgId: string,
			filters?: Record<string, unknown>,
			pagination?: Record<string, unknown>,
			viewMode?: string,
		) => ["investments", orgId, "list", filters, pagination, viewMode] as const,
		detail: (orgId: string, uuid: string) =>
			["investments", orgId, "detail", uuid] as const,
		documents: (orgId: string, uuid: string) =>
			["investments", orgId, "documents", uuid] as const,
	},
	dashboard: {
		all: (orgId: string) => ["dashboard", orgId] as const,
		metrics: (orgId: string, filters?: DashboardMetricsParams) =>
			["dashboard", orgId, "metrics", filters] as const,
	},
	pipeline: {
		all: (orgId: string) => ["pipeline", orgId] as const,
		query: (
			orgId: string,
			params?: { promoter_uuid?: string; fiscal_year?: number },
		) => ["pipeline", orgId, "query", params] as const,
		metrics: (orgId: string, params?: Record<string, unknown>) =>
			["pipeline", orgId, "metrics", params] as const,
	},
	templates: {
		all: (orgId: string) => ["templates", orgId] as const,
		list: (orgId: string, filters?: TemplateFilters) =>
			["templates", orgId, "list", filters] as const,
		detail: (orgId: string, uuid: string) =>
			["templates", orgId, "detail", uuid] as const,
		content: (orgId: string, uuid: string) =>
			["templates", orgId, "content", uuid] as const,
	},
	proposals: {
		all: () => ["proposals"] as const,
		list: (params?: { limit?: number; offset?: number }) =>
			["proposals", "list", params] as const,
		detail: (uuid: string, token: string) =>
			["proposals", "detail", uuid, token] as const,
	},
	hub: {
		all: ["hub"] as const,
		profile: () => ["hub", "profile"] as const,
		dashboard: () => ["hub", "dashboard"] as const,
		pendingActions: (params?: Record<string, unknown>) =>
			["hub", "pendingActions", params] as const,
		investments: (params: Record<string, unknown>) =>
			["hub", "investments", params] as const,
		investmentDetail: (uuid: string) =>
			["hub", "investmentDetail", uuid] as const,
		investmentDocuments: (investmentUuid: string) =>
			["hub", "investmentDocuments", investmentUuid] as const,
	},
	contracts: {
		all: (orgId: string) => ["contracts", orgId] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["contracts", orgId, "list", params] as const,
		metrics: (orgId: string) => ["contracts", orgId, "metrics"] as const,
	},
	notifications: {
		all: (orgId: string) => ["notifications", orgId] as const,
		lists: (orgId: string) => ["notifications", orgId, "list"] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["notifications", orgId, "list", params] as const,
		stats: (orgId: string) => ["notifications", orgId, "stats"] as const,
	},
	promoters: {
		all: (orgId: string) => ["promoters", orgId] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["promoters", orgId, "list", params] as const,
		detail: (orgId: string, id: string) =>
			["promoters", orgId, "detail", id] as const,
		documents: (
			orgId: string,
			uuid: string,
			params?: Record<string, unknown>,
		) =>
			params !== undefined
				? (["promoters", orgId, "documents", uuid, params] as const)
				: (["promoters", orgId, "documents", uuid] as const),
		metrics: (orgId: string, params?: Record<string, unknown>) =>
			["promoters", orgId, "metrics", params] as const,
	},
	investorDropdown: {
		all: (orgId: string) => ["investorDropdown", orgId] as const,
		search: (orgId: string, search: string) =>
			["investorDropdown", orgId, search] as const,
	},
	promoterDropdown: {
		all: (orgId: string) => ["promoterDropdown", orgId] as const,
		search: (orgId: string, search: string) =>
			["promoterDropdown", orgId, search] as const,
	},
	investors: {
		all: (orgId: string) => ["investors", orgId] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["investors", orgId, "list", params] as const,
		detail: (orgId: string, uuid: string, type: string) =>
			["investors", orgId, "detail", uuid, type] as const,
		investments: (
			orgId: string,
			uuid: string,
			filters?: Record<string, unknown>,
		) => ["investors", orgId, "investments", uuid, filters] as const,
		investmentMetrics: (orgId: string, uuid: string) =>
			["investors", orgId, "investmentMetrics", uuid] as const,
		documents: (orgId: string, uuid: string) =>
			["investors", orgId, "documents", uuid] as const,
		metrics: (orgId: string, params?: Record<string, unknown>) =>
			["investors", orgId, "metrics", params] as const,
		dashboardMetrics: (orgId: string, params?: Record<string, unknown>) =>
			["investors", orgId, "dashboardMetrics", params] as const,
	},
	projects: {
		all: (orgId: string) => ["projects", orgId] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["projects", orgId, "list", params] as const,
		detail: (projectId: string) => ["projects", "detail", projectId] as const,
		query: (queryId: string, params?: Record<string, unknown>) =>
			["projects", "query", queryId, params] as const,
		promoterProjects: (orgId: string, promoterUuid: string) =>
			["projects", orgId, "promoterProjects", promoterUuid] as const,
		promoterProjectsList: (
			orgId: string,
			promoterUuid: string,
			params?: Record<string, unknown>,
		) =>
			params !== undefined
				? ([
						"projects",
						orgId,
						"promoterProjects",
						promoterUuid,
						"list",
						params,
					] as const)
				: ([
						"projects",
						orgId,
						"promoterProjects",
						promoterUuid,
						"list",
					] as const),
		poster: (projectId: string) =>
			["projects", "detail", projectId, "poster"] as const,
	},
	userProfile: {
		all: () => ["userProfile"] as const,
		current: () => ["userProfile", "current"] as const,
	},
	modules: {
		all: (orgId: string) => ["modules", orgId] as const,
		entitlements: (orgId: string) =>
			["modules", orgId, "entitlements"] as const,
	},
	organization: {
		all: (orgId: string) => ["organization", orgId] as const,
		userOrganizations: () => ["organization", "userOrganizations"] as const,
		details: (orgId: string) => ["organization", orgId, "details"] as const,
		users: (orgId: string) => ["organization", orgId, "users"] as const,
		invitations: (orgId: string) =>
			["organization", orgId, "invitations"] as const,
		certificates: (orgId: string) =>
			["organization", orgId, "certificates"] as const,
		templates: (orgId: string) => ["organization", orgId, "templates"] as const,
	},
};
